import { useCallback, useState, useEffect } from "react";
import { useApi } from "./use-api";
import { useSingleton } from "./use-singleton";
import { useNotify } from "./use-notify";
import {
  COOKIE_ACCESS_MAIL,
  COOKIE_ACCESS_ROLE,
  COOKIE_ACCESS_BOARD,
  COOKIE_ACCESS_EXAM,
  COOKIE_ACCESS_MEDIUM,
  COOKIE_ACCESS_SESSION,
  COOKIE_ACCESS_SESSION_EXPIRY,
  COOKIE_ACCESS_SUBSCRIPTION_STATUS,
  COOKIE_ACCESS_SECTION,
  COOKIE_ACCESS_SUBSCRIBED_COURSES,
} from "../utils/storage-constants";
import {
  API_LOGIN,
  API_RESET_PASSWORD,
  API_LOGOUT,
  API_LEARNING_PREFERENCE,
  API_TEMP_LOGIN,
} from "../utils/api-routes";

/**
 * Unified Auth Hook for scalable and secure session management.
 * Centralizes all login, logout, and persistence logic.
 */
export const useAuth = () => {
  const { post } = useApi();
  const { toggleNotify } = useNotify();
  const {
    mail,
    updateRole,
    updateMail,
    updateBoard,
    updateExam,
    updateMedium,
    updateSession,
    updateSection,
    updateSubscription,
  } = useSingleton();

  const [isLoading, setIsLoading] = useState(false);

  /**
   * Safe persistence helper to handle localStorage errors and consistency.
   */
  const persistSession = useCallback(
    (apiData) => {
      const {
        role,
        mail,
        board,
        target_exam,
        focus,
        medium,
        token,
        access_token,
        session_time,
        subscription_status,
        section,
        subscribed_courses,
      } = apiData;

      // Standardize exam target key
      const examTarget = focus || target_exam;

      // Persistence in LocalStorage
      if (role) localStorage.setItem(COOKIE_ACCESS_ROLE, role);
      if (mail) localStorage.setItem(COOKIE_ACCESS_MAIL, mail);
      if (board) localStorage.setItem(COOKIE_ACCESS_BOARD, board);
      if (examTarget) localStorage.setItem(COOKIE_ACCESS_EXAM, examTarget);
      if (medium) localStorage.setItem(COOKIE_ACCESS_MEDIUM, medium);
      if (section) localStorage.setItem(COOKIE_ACCESS_SECTION, section);
      if (session_time) {
        localStorage.setItem(COOKIE_ACCESS_SESSION, session_time);

        // Convert backend UTC time into timestamp
        const expiryTime = new Date(session_time).getTime();

        localStorage.setItem(
          COOKIE_ACCESS_SESSION_EXPIRY,
          expiryTime.toString(),
        );
      }
      if (subscription_status)
        localStorage.setItem(
          COOKIE_ACCESS_SUBSCRIPTION_STATUS,
          subscription_status,
        );

      // Ensure Bearer token is saved for Axios interceptor
      const activeToken = token || access_token;
      if (activeToken) localStorage.setItem("token", activeToken);

      if (subscribed_courses) {
        localStorage.setItem(
          COOKIE_ACCESS_SUBSCRIBED_COURSES,
          JSON.stringify(subscribed_courses || []),
        );
      }

      // Update Application Context State
      if (role) updateRole(role);
      if (mail) updateMail(mail);
      if (board) updateBoard(board);
      if (examTarget) updateExam(examTarget);
      if (medium) updateMedium(medium);
      if (session_time) updateSession(session_time);
      if (section) updateSection(section);
    },
    [
      updateRole,
      updateMail,
      updateBoard,
      updateExam,
      updateMedium,
      updateSession,
      updateSection,
      updateSubscription,
    ],
  );

  /**
   * Centralized login logic.
   */
  const login = useCallback(
    async (email, password) => {
      if (!email || !password) return null;

      setIsLoading(true);
      try {
        const response = await post(API_TEMP_LOGIN, {
          identifier: email.trim(),
          password: password.trim(),
        });

        const apiData = response?.data;

        if (!apiData?.role) {
          throw new Error(
            apiData?.message || "Invalid login response from server",
          );
        }

        persistSession(apiData);

        toggleNotify({
          type: "success",
          title: "Welcome",
          description: apiData.message || "Successfully logged in",
        });

        return apiData;
      } catch (error) {
        const errorMsg =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error.message ||
          "Connection failed";
        toggleNotify({
          type: "error",
          title: "Login Failed",
          description: errorMsg,
        });
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [post, persistSession, toggleNotify],
  );

  const learningPreference = useCallback(
    async (payload) => {
      setIsLoading(true);
      try {
        const response = await post(API_LEARNING_PREFERENCE, {
          payload,
        });

        const apiData = response?.data;

        if (!apiData?.role) {
          throw new Error(
            apiData?.message || "Invalid login response from server",
          );
        }

        persistSession(apiData);

        toggleNotify({
          type: "success",
          title: "Welcome",
          description: apiData.message || "Successfully registered",
        });

        return apiData;
      } catch (error) {
        const errorMsg =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error.message ||
          "Connection failed";
        toggleNotify({
          type: "error",
          title: "Login Failed",
          description: errorMsg,
        });
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [post, persistSession, toggleNotify],
  );

  /**
   * Password reset request.
   */
  const requestPasswordReset = useCallback(
    async (email) => {
      if (!email) return;

      setIsLoading(true);
      try {
        const response = await post(API_RESET_PASSWORD, {
          email: email.trim(),
        });
        toggleNotify({
          type: "success",
          title: "Request Sent",
          description:
            response?.data?.message ||
            "Password reset instructions sent to your email",
        });
        return response.data;
      } catch (error) {
        toggleNotify({
          type: "error",
          title: "Reset Failed",
          description:
            "Unable to process reset request. Please try again later.",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [post, toggleNotify],
  );

  /**
   * Secure Logout - Clears all session data and resets app state.
   */
  const logout = useCallback(
    async (showNotification = true, shouldRedirect = true) => {
      const activeMail =
        mail ||
        (typeof window !== "undefined" &&
          (localStorage.getItem(COOKIE_ACCESS_MAIL) ||
            localStorage.getItem("email") ||
            localStorage.getItem("user_email") ||
            localStorage.getItem("__FANAMA_USER_EMAIL__"))) ||
        "";

      try {
        if (activeMail) {
          await post(API_LOGOUT, { email: activeMail });
        }
      } catch (e) {
        console.warn("Logout API call error:", e);
      } finally {
        // Clear Storage - remove all user credentials, tokens, and roles
        const authKeys = [
          COOKIE_ACCESS_ROLE,
          COOKIE_ACCESS_MAIL,
          COOKIE_ACCESS_BOARD,
          COOKIE_ACCESS_EXAM,
          COOKIE_ACCESS_MEDIUM,
          COOKIE_ACCESS_SESSION,
          COOKIE_ACCESS_SESSION_EXPIRY,
          COOKIE_ACCESS_SECTION,
          COOKIE_ACCESS_SUBSCRIPTION_STATUS,
          COOKIE_ACCESS_SUBSCRIBED_COURSES,
          "token",
          "email",
          "user_email",
          "__FANAMA_USER_EMAIL__",
          "subject",
          "teacher_subject",
          "__FANAMA_SUBJECT__",
          "marks",
        ];
        authKeys.forEach((key) => {
          localStorage.removeItem(key);
        });

        // Clear Session Storage completely
        if (typeof window !== "undefined" && window.sessionStorage) {
          sessionStorage.clear();
        }

        // Clear Context
        updateRole("");
        updateMail("");
        updateBoard("");
        updateExam("");
        updateMedium("");
        updateSession("");
        updateSection("");
        updateSubscription("");

        if (showNotification) {
          toggleNotify({
            type: "success",
            title: "Logged Out",
            description: "You have been securely logged out.",
          });
        }

        // Guaranteed Redirection to Login
        if (shouldRedirect && typeof window !== "undefined") {
          window.location.replace("/");
        }
      }
    },
    [
      mail,
      updateRole,
      updateMail,
      updateBoard,
      updateExam,
      updateMedium,
      updateSession,
      updateSection,
      updateSubscription,
      post,
      toggleNotify,
    ],
  );

  const apiLogout = async () => {
    try {
      const res = await post(API_LOGOUT, { email: mail });
      if (res) {
        console.log(res?.data);
      }
    } catch (e) {
      console.log("ERROR COMES FROM APILOGUT", e);
    }
  };

  useEffect(() => {
    const role = localStorage.getItem(COOKIE_ACCESS_ROLE);
    const expiry = localStorage.getItem(COOKIE_ACCESS_SESSION_EXPIRY);

    if (!role || !expiry) return;

    const remainingTime = Number(expiry) - Date.now();

    // Already expired
    if (remainingTime <= 0) {
      logout(false, true);
      return;
    }

    // Auto logout timer
    const timer = setTimeout(() => {
      logout(false, true);
    }, remainingTime);

    return () => clearTimeout(timer);
  }, [logout]);

  return {
    login,
    logout,
    requestPasswordReset,
    learningPreference,
    persistSession,
    isLoading,
  };
};
